import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';
import type { NormalizedEntityWriteMeta } from './normalized-entity-write.js';
import { writeNormalizedEntitySnapshot } from './normalized-entity-write.js';

const retainedEntityPrefixes = new WeakMap<QueryClient, Set<string>>();

export type EntityProjectionLiteral = string | number | boolean | null;

/** Maps one public DTO onto a canonical entity resource. Field aliases keep
 * incompatible route shapes (for example winner `pubkey` = market) out of the
 * shared entity while preserving the generated hook's public response type. */
export interface EntityProjectionSpec {
  readonly resource: string;
  readonly fieldMap?: Readonly<Record<string, string>>;
  readonly canonicalIdField?: string;
  readonly literals?: Readonly<Record<string, EntityProjectionLiteral>>;
  /** Route/index-local fields that must stay on the normalized list envelope
   * instead of being merged into the shared canonical entity. */
  readonly indexFields?: readonly string[];
}

/** A nested public DTO array stored as stable references on its parent while
 * the complete rows live in the canonical entity resource. */
export interface NestedEntityProjectionSpec extends EntityProjectionSpec {
  readonly field: string;
  readonly idField: string;
  readonly referenceFields: readonly string[];
  readonly parentIdField?: string;
  readonly canonicalParentField?: string;
}

export interface OutcomeEntityDiagnostic {
  readonly kind:
    | 'identity-mismatch'
    | 'stale-sequence-rejected'
    | 'lifecycle-transition-failure'
    | 'projection-divergence'
    | 'missing-entity-recovery-failure';
  readonly entityHash: string;
  readonly marketHash?: string;
  readonly field?: string;
  readonly source?: string;
}

export function projectionEntityQueryKey(spec: EntityProjectionSpec, id: string): QueryKey {
  return [spec.resource, 'entity', id];
}

export function canonicalizeEntityRow(
  row: Record<string, unknown>,
  spec: EntityProjectionSpec | undefined,
  id?: string,
): Record<string, unknown> {
  if (spec === undefined) return row;
  const canonical: Record<string, unknown> = {};
  for (const [sourceField, value] of Object.entries(row)) {
    if (spec.indexFields?.includes(sourceField) === true) continue;
    const canonicalField = spec.fieldMap?.[sourceField] ?? sourceField;
    if (
      process.env.NODE_ENV !== 'production' &&
      canonical[canonicalField] !== undefined &&
      canonical[canonicalField] !== value
    ) {
      recordOutcomeEntityDiagnostic({
        kind: 'projection-divergence',
        entityHash: hashDiagnosticEntity(id ?? spec.resource),
        field: canonicalField,
        source: sourceField,
      });
    }
    canonical[canonicalField] = value;
  }
  if (spec.literals !== undefined) Object.assign(canonical, spec.literals);
  if (spec.canonicalIdField !== undefined && id !== undefined) {
    canonical[spec.canonicalIdField] = id;
  }
  return canonical;
}

export function projectEntityRow<Row>(
  canonical: Row,
  spec: EntityProjectionSpec | undefined,
  indexFields?: Readonly<Record<string, unknown>>,
): Row {
  if (canonical === null || typeof canonical !== 'object') return canonical;
  if (spec === undefined && indexFields === undefined) return canonical;
  const projected = { ...(canonical as Record<string, unknown>) };
  for (const [sourceField, canonicalField] of Object.entries(spec?.fieldMap ?? {})) {
    if (canonicalField in projected) projected[sourceField] = projected[canonicalField];
  }
  if (indexFields !== undefined) Object.assign(projected, indexFields);
  return projected as Row;
}

export function projectEntityField(
  canonical: Record<string, unknown>,
  sourceField: string,
  spec: EntityProjectionSpec | undefined,
  indexFields?: Readonly<Record<string, unknown>>,
): unknown {
  if (spec?.indexFields?.includes(sourceField) === true) return indexFields?.[sourceField];
  return canonical[spec?.fieldMap?.[sourceField] ?? sourceField];
}

export function pickEntityIndexFields(
  row: Readonly<Record<string, unknown>>,
  spec: EntityProjectionSpec | undefined,
): Readonly<Record<string, unknown>> | undefined {
  if (spec?.indexFields === undefined) return undefined;
  const picked: Record<string, unknown> = {};
  for (const field of spec.indexFields) {
    if (row[field] !== undefined) picked[field] = row[field];
  }
  return Object.keys(picked).length === 0 ? undefined : picked;
}

export function splitNestedEntityProjection<Row>(
  queryClient: QueryClient,
  parent: Row,
  spec: NestedEntityProjectionSpec | undefined,
  writeMeta?: NormalizedEntityWriteMeta,
  skipEntityIds?: ReadonlySet<string>,
): Row {
  if (spec === undefined || parent === null || typeof parent !== 'object') return parent;
  const parentRow = parent as Record<string, unknown>;
  const nested = parentRow[spec.field];
  if (nested === null || typeof nested !== 'object') return parent;

  retainProjectedEntities(queryClient, spec);

  const parentId = spec.parentIdField === undefined ? undefined : parentRow[spec.parentIdField];
  const refs: Record<string, unknown>[] = [];
  for (const value of Array.isArray(nested) ? nested : [nested]) {
    if (value === null || typeof value !== 'object') continue;
    const row = value as Record<string, unknown>;
    const rawId = row[spec.idField];
    if (typeof rawId !== 'string' || rawId.length === 0) {
      recordOutcomeEntityDiagnostic({
        kind: 'lifecycle-transition-failure',
        entityHash: hashDiagnosticEntity(spec.field),
        source: 'missing-nested-id',
      });
      continue;
    }
    const withParent = { ...row };
    if (
      spec.canonicalParentField !== undefined &&
      typeof parentId === 'string' &&
      parentId.length > 0
    ) {
      const currentParent = withParent[spec.canonicalParentField];
      if (
        typeof currentParent === 'string' &&
        currentParent.length > 0 &&
        currentParent !== parentId
      ) {
        recordOutcomeEntityDiagnostic({
          kind: 'identity-mismatch',
          entityHash: hashDiagnosticEntity(rawId),
          marketHash: hashDiagnosticEntity(parentId),
          field: spec.canonicalParentField,
          source: 'nested-projection',
        });
        continue;
      }
      withParent[spec.canonicalParentField] = parentId;
    }
    if (skipEntityIds?.has(rawId) !== true) {
      const canonical = canonicalizeEntityRow(withParent, spec, rawId);
      writeNormalizedEntitySnapshot(
        queryClient,
        projectionEntityQueryKey(spec, rawId),
        canonical,
        writeMeta,
        { skipEqual: true },
      );
    }
    refs.push(pickReferenceFields(row, spec));
  }
  return { ...parentRow, [spec.field]: Array.isArray(nested) ? refs : (refs[0] ?? null) } as Row;
}

export function materializeNestedEntityProjection<Row>(
  queryClient: QueryClient,
  parent: Row,
  spec: NestedEntityProjectionSpec | undefined,
): Row {
  if (spec === undefined || parent === null || typeof parent !== 'object') return parent;
  const parentRow = parent as Record<string, unknown>;
  const refs = parentRow[spec.field];
  if (refs === null || typeof refs !== 'object') return parent;
  const rows: Record<string, unknown>[] = [];
  for (const value of Array.isArray(refs) ? refs : [refs]) {
    if (value === null || typeof value !== 'object') continue;
    const ref = value as Record<string, unknown>;
    const rawId = ref[spec.idField];
    if (typeof rawId !== 'string' || rawId.length === 0) continue;
    const canonical = queryClient.getQueryData<Record<string, unknown>>(
      projectionEntityQueryKey(spec, rawId),
    );
    if (canonical === undefined) {
      rows.push(ref);
    } else {
      rows.push({ ...projectEntityRow(canonical, spec), ...ref });
    }
  }
  return { ...parentRow, [spec.field]: Array.isArray(refs) ? rows : (rows[0] ?? null) } as Row;
}

export function nestedEntityIds(
  parent: unknown,
  spec: NestedEntityProjectionSpec | undefined,
): readonly string[] {
  if (spec === undefined || parent === null || typeof parent !== 'object') return [];
  const nested = (parent as Record<string, unknown>)[spec.field];
  if (nested === null || typeof nested !== 'object') return [];
  const ids: string[] = [];
  for (const value of Array.isArray(nested) ? nested : [nested]) {
    if (value === null || typeof value !== 'object') continue;
    const id = (value as Record<string, unknown>)[spec.idField];
    if (typeof id === 'string' && id.length > 0) ids.push(id);
  }
  return ids;
}

export function hasNestedEntitySnapshots(
  parent: unknown,
  spec: NestedEntityProjectionSpec | undefined,
): boolean {
  if (spec === undefined || parent === null || typeof parent !== 'object') return false;
  const nested = (parent as Record<string, unknown>)[spec.field];
  if (nested === null || typeof nested !== 'object') return false;
  const refFields = new Set([spec.idField, ...spec.referenceFields]);
  return (Array.isArray(nested) ? nested : [nested]).some(
    (value) =>
      value !== null &&
      typeof value === 'object' &&
      Object.keys(value).some((field) => !refFields.has(field)),
  );
}

export function recordOutcomeEntityDiagnostic(diagnostic: OutcomeEntityDiagnostic): void {
  emit('outcome-entity:diagnostic', diagnostic);
}

function retainProjectedEntities(queryClient: QueryClient, spec: EntityProjectionSpec): void {
  const prefix = [spec.resource, 'entity'] as const;
  const prefixHash = JSON.stringify(prefix);
  let retained = retainedEntityPrefixes.get(queryClient);
  if (retained === undefined) {
    retained = new Set<string>();
    retainedEntityPrefixes.set(queryClient, retained);
  }
  if (retained.has(prefixHash)) return;
  queryClient.setQueryDefaults(prefix, { gcTime: Number.POSITIVE_INFINITY });
  retained.add(prefixHash);
}

function pickReferenceFields(
  row: Record<string, unknown>,
  spec: NestedEntityProjectionSpec,
): Record<string, unknown> {
  const ref: Record<string, unknown> = { [spec.idField]: row[spec.idField] };
  for (const field of spec.referenceFields) {
    if (row[field] !== undefined) ref[field] = row[field];
  }
  return ref;
}
